import './list-view.scss';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ImageGenerator from '../image-generator/image-generator';
import { Link, useLocation } from 'react-router-dom';
import { render_object_type } from '../../shared/services/general/configs';
import MovieCard from '../card/movie-card/movie-card';
import SeriesCard from '../card/series-card/series-card';
import Breadcrumb from '../breadcrumb/breadcrumb';
import { useConfigApp } from '../../shared/services/general/app-context';
import { useSeo } from '../../shared/services/general/seoManager';
import SliderCarousel from '../carousel/slider/slider-carousel';

const BREADCRUMB_TYPES = {
	category: { label: 'دسته بندی ها', path: '/category' },
	genre: { label: 'ژانر ها', path: '/genre' },
	poster: { label: 'پوستر', path: '/category' },
	home: { label: 'خانه', path: '/' }
};

const getPathType = (path) => {
	if (path.includes('/category/')) return 'category';
	else if (path.includes('/genre/')) return 'genre';
	else if (path.includes('/p/movies')) return 'movies';
	else if (path.includes('/p/SERIES')) return 'series';
	else if (path.includes('/t/poster')) return 'poster';
	else return 'default';
};

const extractIdFromPath = (path, prefix) => {
	return path.split(prefix)[1]?.split('/')[0];
};

function ListView({ data, type }) {
	const [pageName, setPageName] = useState(null);
	const [breadcrumbType, setBreadcrumbType] = useState('');
	const [logo, setLogo] = useState(null);
	const [contents, setContents] = useState([]);

	const location = useLocation();
	const configApp = useConfigApp();
	const { setSeoData } = useSeo();

	const path = location.pathname;
	const pathType = useMemo(() => getPathType(path), [path]);

	const getValueById = useCallback((id, source = 'catList') => {
		try {
			const storedList = JSON.parse(localStorage.getItem(source)) || [];
			const targetItem = storedList.find(item => item.id === id);
			return targetItem?.value || null;
		} catch (error) {
			console.error('Error parsing localStorage data:', error);
			return null;
		}
	}, []);

	useEffect(() => {
		if (configApp?.logo) {
			try {
				setLogo(require(`../../assets/images/${configApp.logo}`).default);
			} catch (error) {
				console.error('Error loading logo:', error);
			}
		}
	}, [configApp]);

	useEffect(() => {
		const processPathData = () => {
			switch (pathType) {
				case 'category':
					setPageName(data?.category?.name);
					setBreadcrumbType(BREADCRUMB_TYPES.category);
					setContents({
						contents: data?.contents,
						slider: data?.slider
					});
					break;

				case 'genre':
					setPageName(data?.genre?.name);
					setBreadcrumbType(BREADCRUMB_TYPES.genre);
					setContents({ contents: data?.contents });
					break;

				case 'movies':
					const movieId = extractIdFromPath(path, '/p/movies/');
					setPageName(getValueById(movieId, 'catList'));
					setBreadcrumbType(BREADCRUMB_TYPES.category);
					setContents({ contents: data });
					break;

				case 'series':
					const seriesId = extractIdFromPath(path, '/p/SERIES/');
					setPageName(getValueById(seriesId, 'catList'));
					setBreadcrumbType(BREADCRUMB_TYPES.category);
					setContents({ contents: data });
					break;

				case 'poster':
					setPageName(data?.titlePages);
					setBreadcrumbType(BREADCRUMB_TYPES.home);
					setContents({ contents: data?.contents });
					break;

				default:
					setContents({ contents: data });
					break;
			}
		};

		if (data) {
			processPathData();
		}
	}, [data, pathType, path, getValueById]);

	useEffect(() => {
		if (pageName && configApp?.title) {
			setSeoData({
				title: `${pageName} | ${configApp.title}`,
				image: logo ? `${window.location.origin}${logo}` : null,
				description: null,
			});
		}
	}, [pageName, logo, configApp, setSeoData]);

	const renderCard = useCallback((item, index) => {
		const cardType = type || item?.type;

		if (cardType === 'movies' || cardType === render_object_type.movie) {
			return <MovieCard key={index} data={item} />;
		}

		if (cardType === 'series' || cardType === render_object_type.series) {
			return <SeriesCard key={index} data={item} />;
		}

		if (cardType === 'shows' || cardType === render_object_type.show) {
			return (
				<Link key={index} to={`/shows/${item.id}`} className="card">
					<ImageGenerator src={item.image} name={item.title} />
					<div className="card-info">
						<span>{item.title}</span>
						<span>{item.director}</span>
					</div>
				</Link>
			);
		}

		if (!type && item?.type === render_object_type.series) {
			return <SeriesCard key={index} data={item} />;
		}

		if (!type && item?.type === render_object_type.movie) {
			return <MovieCard key={index} data={item} />;
		}

		if (!type && !item?.type) {
			return null;
		}

		return (
			<Link key={index} to={`/t/${item.tagDefinitionName}/${item.id}`} className="card">
				<ImageGenerator src={item.fields?.image} name={item.value} />
				<div className="card-info">
					<span>{item.value}</span>
					<span>{item.director}</span>
				</div>
			</Link>
		);
	}, [type]);

	const shouldShowSlider = pathType === 'category' && contents?.slider;
	const hasContents = contents?.contents?.length > 0;

	return (
		<div className="main-page tag-page">
			{pageName && (
				<div style={{ paddingRight: '20px' }}>
					<Breadcrumb
						style={{ marginBottom: '-22px' }}
						firstItem={breadcrumbType || undefined}
						currentItem={pageName}
					/>
					<br />
					<h1>{pageName}</h1>
					<br />
				</div>
			)}

			{shouldShowSlider && (
				<SliderCarousel
					data={contents.slider}
					baseUrl={type}
				/>
			)}

			<div className="box-of-items">
				{hasContents && contents.contents.map(renderCard)}
			</div>
		</div>
	);
}

export default ListView;